var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ActivityManagerTyped as ActivityManager } from './ActivityManager';
import AssetLoader from './AssetLoader';
import ErrorLogger from './ErrorLogger';
import AccessibilityHelper from './AccessibilityHelper';
import TypingActivityHelper from './TypingActivityHelper';
import { TweenMax, Draggable, TweenLite, Power0 } from 'gsap/all';
import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';
import mixins from 'vue-typed-mixins';
import _ from 'lodash';
const HINT = 'HINT';
const INCORRECT_ATTEMPT = 'INCORRECT_ATTEMPT';
const TYPE_OVER_TEXT = 'TYPE_OVER_TEXT';
export default () => mixins(AssetLoader, ActivityManager(), AccessibilityHelper, TypingActivityHelper, ErrorLogger).extend({
    data() {
        return {
            correctDropAreaId: 0,
            closestDropZoneId: 0,
            correctGuesses: [],
            defaultColor: 'rgba(135, 203, 222, 0.7)',
            hintBorderColor: '',
            guessed: [],
            isSubmitted: false,
            showCorrectAnswers: false,
            shouldShowHint: false,
            shouldShowKeyboard: false,
            wrongGuesses: [],
            selectedElement: null,
            audiosMap: {}
        };
    },
    methods: {
        // start activity helper methods
        getActivityState() {
            const { rounds } = this.activityData;
            return {
                rounds,
                incorrectAttempt: this.activityData.incorrectAttempt,
                instructions: Object.assign(Object.assign({}, rounds[0].instructions), { text: rounds[0].instructions.audio.description })
            };
        },
        getKeyboardConfigHelper() {
            const { keyboardType, roofs } = this.activityData.keyboard;
            return {
                keyboardType,
                hideNumberRow: false,
                showKeyboardAtStart: false,
                roofs
            };
        },
        loadActivityHelper() {
            var _a, _b, _c;
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore implemented by consumer
                const activityState = this.getActivityState();
                this.createRounds(activityState);
                this.audiosMap = {
                    [HINT]: (_a = activityState.rounds[0].hint.audio.src) !== null && _a !== void 0 ? _a : '',
                    [INCORRECT_ATTEMPT]: (_b = activityState.incorrectAttempt.audio.src) !== null && _b !== void 0 ? _b : '',
                    [TYPE_OVER_TEXT]: (_c = activityState.rounds[0].typeOverText.instructions.audio.src) !== null && _c !== void 0 ? _c : ''
                };
                this.sfxDescriptions = {
                    [HINT]: activityState.rounds[0].hint.audio.description,
                    [INCORRECT_ATTEMPT]: activityState.incorrectAttempt.audio.description,
                    [TYPE_OVER_TEXT]: activityState.rounds[0].typeOverText.instructions.audio.description
                };
                this.sfx = yield this.loadAAAudios(this.audiosMap);
            });
        },
        loadAudiosHelper(activityAudios) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPaths = activityAudios.map(({ audio }) => audio.src);
                this.queueAudio(soundPaths);
                const cache = yield this.downloadAudio();
                return activityAudios.map(({ audio }) => cache.get(audio.src));
            });
        },
        setRoundHelper() {
            this.setupNewRound();
            this.errorLoggerStart();
            this.$nextTick(() => {
                TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
                this.createDraggables();
            });
        },
        // end activity helper methods
        createDraggables() {
            let bounds = null;
            const self = this;
            const { isMobile } = this.userDevice;
            this.currentRound.draggableItems.forEach(dragContent => {
                let hasHitBoundary = false;
                Draggable.create(`#draggable-${dragContent.id}`, {
                    autoScroll: 1,
                    cursor: 'grab',
                    activeCursor: 'grabbing',
                    bound: document.querySelector('.activity-wrapper'),
                    onPress() {
                        if (bounds)
                            return;
                        // get DOMRect bounding
                        bounds = getActivityBoundings();
                    },
                    onDrag() {
                        // work-around for iOS not firing touchend on tablet
                        const { pointerX, pointerY, target } = this;
                        // @ts-ignore implemented by consumer
                        if (self.numberOfAttempts[0] > 1 && self.checkIfCloseToDropZone) {
                            // @ts-ignore implemented by consumer
                            self.checkIfCloseToDropZone(pointerX, pointerY, target);
                        }
                        if (!isMobile)
                            return;
                        const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds);
                        if (hasTouched && !hasHitBoundary) {
                            hasHitBoundary = true;
                            // @ts-ignore implemented by consumer
                            self.handleDrop(this);
                        }
                    },
                    onDragStart() {
                        if (self.numberOfAttempts[0] > 1) {
                            self.shouldShowHint = true;
                            if (!self.currentRound.dropZones || !self.currentRound.dropZones.length)
                                return;
                            const droppableItem = self.currentRound.dropZones.find(dropContent => dropContent.correctItemIds.includes(+dragContent.id.toString()));
                            if (!droppableItem)
                                return;
                            self.correctDropAreaId = droppableItem.id;
                        }
                    },
                    onDragEnd() {
                        self.shouldShowHint = false;
                        hasHitBoundary = false;
                        // @ts-ignore implemented by consumer
                        self.handleDrop(this);
                    }
                });
            });
        },
        disableDraggableCorrectGuesses() {
            this.correctGuesses.forEach(correctGuess => {
                Draggable.get(`#draggable-${correctGuess.draggableItem.id}`).disable();
            });
        },
        disableOrEnableDraggableGuesses(shouldDisable) {
            this.guessed.forEach(guess => {
                const draggableItem = Draggable.get(`#draggable-${guess.draggableItem.id}`);
                if (!draggableItem)
                    return;
                if (shouldDisable) {
                    draggableItem.disable();
                    return;
                }
                draggableItem.enable();
            });
        },
        getAllDroppableAreas() {
            const droppableAreas = Array.from(document.querySelectorAll('[id^="dropArea"]'));
            if (!droppableAreas.length)
                return;
            const formattedDropAreas = droppableAreas.map(droppableArea => {
                return {
                    id: droppableArea.id,
                    position: droppableArea.getBoundingClientRect()
                };
            });
            return _.orderBy(formattedDropAreas, 'id');
        },
        getAssociatedDraggableItem(draggableId) {
            return this.currentRound.draggableItems.find(item => item.id === +draggableId);
        },
        getAssociatedHintItem(draggableId, alertMsg) {
            const hintItem = this.currentRound.draggableItems.find(item => item.id === +draggableId);
            this.setScreenReaderAlert(alertMsg.replace('%', hintItem === null || hintItem === void 0 ? void 0 : hintItem.label));
            return hintItem;
        },
        getAssociatedDroppableItem(dropId) {
            var _a;
            return (_a = this.currentRound.dropZones) === null || _a === void 0 ? void 0 : _a.find(item => item.id === +dropId);
        },
        getDroppableItemClass() {
            return this.isSubmitted ? ['droppableItemSubmitted'] : ['droppableItemDefault'];
        },
        getDroppableBackground(value) {
            // @ts-ignore implemented by consumer
            const isGuessedCorrectly = this.isGuessedCorrectly(value);
            // @ts-ignore implemented by consumer
            const isGuessedIncorrectly = this.isGuessedIncorrectly(value);
            if (isGuessedCorrectly && this.isSubmitted)
                return '#7BD189';
            if (isGuessedIncorrectly && this.isSubmitted)
                return '#EA0520';
            return 'rgba(228, 228, 228, 0.7)';
        },
        handleCorrectAnswer(guessedAnswer) {
            this.correctGuesses = _.uniq([...this.correctGuesses, guessedAnswer]);
        },
        handleCorrectKey(attempt) {
            this.sndType();
            this.incrementTypingIndex();
            this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);
            if (this.isEndOfRound) {
                this.sndBell();
                this.processEndOfActivity();
            }
        },
        handleHint() {
            this.playAudio(HINT);
            TweenMax.delayedCall(1, () => {
                this.showCorrectAnswers = true;
            });
        },
        handleOnActivitySubmit() {
            if (this.isSubmitted || this.shouldShowKeyboard) {
                return;
            }
            this.isSubmitted = true;
            this.incrementNumberOfAttempts();
            this.disableOrEnableDraggableGuesses(true);
            // @ts-ignore implemented by consumer
            if (this.hasGuessedAllAnswers)
                return;
            if (!this.guessed.length && this.numberOfAttempts[0] <= 1) {
                this.playAudio(INCORRECT_ATTEMPT);
            }
            this.guessed.forEach(guessedAnswer => {
                // const isCorrect = guessedAnswer.draggableItem.label === guessedAnswer.dropAreaId;
                // @ts-ignore implemented by consumer
                const isCorrect = this.getIsCorrect(guessedAnswer);
                if (isCorrect) {
                    this.handleCorrectAnswer(guessedAnswer);
                    return;
                }
                this.handleWrongAnswer(guessedAnswer);
            });
            TweenMax.delayedCall(1.5, () => {
                this.handleResetAfterSubmit();
            });
        },
        handleWrongAnswer(guessedAnswer) {
            if (this.numberOfAttempts[0] <= 1) {
                this.playAudio(INCORRECT_ATTEMPT);
            }
            this.wrongGuesses = _.uniq([...this.wrongGuesses, guessedAnswer]);
        },
        handleResetAfterSubmit() {
            this.disableOrEnableDraggableGuesses(false);
            this.isSubmitted = false;
            this.guessed = [];
            this.disableDraggableCorrectGuesses();
            this.resetWrongPieces();
            // @ts-ignore implemented by consumer
            if (this.hasGuessedAllAnswers) {
                this.overwriteProgressText({
                    currentText: this.currentRound.typeOverText.text
                });
                this.setInstructions({
                    audio: {
                        src: this.audiosMap[TYPE_OVER_TEXT]
                    },
                    text: this.sfxDescriptions[TYPE_OVER_TEXT]
                });
                this.shouldShowKeyboard = true;
                this.showCorrectAnswers = true;
                this.enableKeyboard();
                this.showKeyboard();
                setTimeout(() => {
                    this.setActivityContainerHeight();
                }, 750);
                return;
            }
            this.wrongGuesses = [];
            // @ts-ignore implemented by consumer
            if (this.numberOfAttempts[0] >= 2 && !this.hasGuessedAllAnswers)
                this.handleHint();
        },
        moveDraggableToDropZone(target, x, y, animationLength = 0.5) {
            TweenLite.to(target, animationLength, {
                x,
                y,
                ease: Power0.easeNone
            });
        },
        removeFromGuessed(draggableItem) {
            this.guessed = [...this.guessed].filter(item => item.draggableItem.id !== +draggableItem.id);
        },
        resetDraggable(target) {
            TweenLite.to(target, 0.5, {
                x: 0,
                y: 0,
                ease: Power0.easeNone
            });
        },
        resetWrongPieces() {
            this.wrongGuesses.forEach(wrongGuess => {
                const draggableItem = document.getElementById(`draggable-${wrongGuess.draggableItem.id}`);
                if (!draggableItem)
                    return;
                this.resetDraggable(draggableItem);
            });
            this.selectedElement = null;
        },
        // keyboard accessibility methods
        handleOnDraggableItemEnterEvent(fractionSelector, useDataId = false) {
            var _a;
            let fractionItemEl;
            if (useDataId) {
                fractionItemEl = (_a = document.querySelectorAll(`[data-id="${fractionSelector}"]`)) === null || _a === void 0 ? void 0 : _a[0];
            }
            else {
                fractionItemEl = document.getElementById(fractionSelector);
            }
            this.selectedElement = fractionItemEl;
        },
        handleOnDroppableItemEnterEvent(answerSelector, allowEmptySelectedEl = false) {
            var _a, _b;
            if (!this.selectedElement && !allowEmptySelectedEl)
                return;
            const selectedElement = (_a = this.selectedElement) !== null && _a !== void 0 ? _a : (_b = this.guessed.find(guess => { var _a, _b; return `dropArea-${(_b = (_a = guess === null || guess === void 0 ? void 0 : guess.droppableItem) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : guess.dropAreaId}` === answerSelector; })) === null || _b === void 0 ? void 0 : _b.draggableItem;
            const alreadyGuessed = [...this.guessed, ...this.correctGuesses].some(item => {
                return item.draggableItem.id === +(selectedElement === null || selectedElement === void 0 ? void 0 : selectedElement.id);
            });
            if (alreadyGuessed) {
                const draggableItem = document.getElementById(`draggable-${selectedElement.id}`);
                this.resetDraggable(draggableItem);
                this.removeFromGuessed(selectedElement);
                return;
            }
            const dropAreaEl = document.getElementById(answerSelector);
            // @ts-ignore implemented by consumer
            this.handleDrop({ target: this.selectedElement }, dropAreaEl);
        },
        handleDragContainerEnterEvent() {
            if (!this.selectedElement)
                return;
            const selectedElementId = +this.selectedElement.id.split('-')[1];
            this.resetDraggable(this.selectedElement);
            this.removeFromGuessed({ id: selectedElementId });
        }
    }
});
